import axios from 'axios'
import { useEffect, useReducer, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { getErrorMessage } from '../error-helper'
import config from '../config'
import { styled } from '@mui/system'
import infoLogService from '../services/info.service'
import { delay } from '../helpers/utils'

const RETRY_DELAY = 3000
const TIMEOUT = 3000

const http = axios.create({
  baseURL: config.EID_URL,
  timeout: TIMEOUT,
  headers: {
    Authorization: `Bearer ${config.EID_TOKEN}`,
    'Content-Type': 'application/json',
  },
})

const StyledDiv = styled('div')({
  [`& #videoid-legend a#logo-eid`]: {
    display: 'none',
  },
  [`& #auth-id`]: {
    color: '#9E9E9E',
    fontSize: '10px',
    position: 'absolute',
  },
})

function VideoID({
  retry,
  start,
  idType,
  onCompleted,
  onError,
  onTimeout,
  onFailed,
  onPhaseStarting,
  ...props
}) {
  const [auth, setAuth] = useState()
  const [videoId, setVideoId] = useState()

  const initialState = {
    status: 'idle',
    error: null,
    data: [],
    retryCount: -1,
  }

  const [state, dispatch] = useReducer((state, action) => {
    switch (action.type) {
      case 'FETCHING':
        return { ...initialState, status: 'fetching' }
      case 'FETCHED':
        return { ...initialState, status: 'fetched', data: action.payload }
      case 'FETCH_TIMEOUT':
        return {
          ...initialState,
          status: 'timeout',
          error: action.payload,
          retryCount: action.retryCount,
        }
      case 'FETCH_ERROR':
        return { ...initialState, status: 'error', error: action.payload }
      default:
        return state
    }
  }, initialState)

  const handlePhaseStarting = useCallback(
    (phase) => {
      console.log('phase', phase)
      if (onPhaseStarting) {
        onPhaseStarting(phase)
      } else {
        phase.continue()
      }
    },
    [onPhaseStarting]
  )

  const handleCompleted = useCallback(
    async (video) => {
      infoLogService.log({
        tag: 'eID handleCompleted',
        info: { response: JSON.stringify(video) },
      })
      videoId.turnOff()
      setVideoId(null)
      setAuth(null)
      let currentAttempt = 0
      let errMessage = null
      do {
        try {
          const response = await http.get(
            `${config.EID_URL}/videoid/${video.id}`
          )

          if (onCompleted) {
            onCompleted(response.data)
            errMessage = null
            break
          }
        } catch (error) {
          if (currentAttempt < retry) {
            await delay(RETRY_DELAY)
          } else {
            errMessage = getErrorMessage(error)
          }
        }
        currentAttempt++
      } while (currentAttempt <= retry)

      if (errMessage) {
        dispatch({ type: 'FETCH_ERROR', payload: errMessage })
      }
    },
    [videoId, onCompleted, retry, dispatch]
  )

  const handleFailed = useCallback(
    (res) => {
      infoLogService.log({
        tag: 'eID videoid failed',
        error: JSON.stringify(res),
      })
      videoId.turnOff()
      setVideoId(null)
      setAuth(null)
      onFailed(res?.error?.errorId, res?.notification?.message?.text, auth?.id)
    },
    [onFailed, auth, videoId]
  )

  useEffect(() => {
    let ignore = false
    if (state.status === 'timeout') {
      if (onTimeout && !ignore) {
        onTimeout(state.error, state.retryCount)
      }
    } else if (state.status === 'error') {
      if (onError && !ignore) {
        onError(state.error)
      }
    }
    return () => {
      ignore = true
    }
  }, [state, onError, onTimeout])

  useEffect(() => {
    let ignore = false
    const script = document.createElement('script')
    script.onload = function () {
      if (!ignore) {
        setVideoId(
          window.EID.videoId('#video', {
            lang: 'en',
          })
        )
      }
    }
    script.src = `${config.EID_URL}/js/videoid.js`
    script.async = true
    document.body.appendChild(script)

    return () => {
      ignore = true
    }
  }, [])

  useEffect(() => {
    let ignore = false
    const requestAuth = async () => {
      for (let i = 0; i <= retry; i++) {
        try {
          const response = await http.post(
            `${config.EID_URL}/videoid.request`,
            {}
          )
          if (!ignore) {
            infoLogService.log({
              tag: 'eID videoid.request API',
              info: { response: JSON.stringify(response) },
            })
            setAuth(response.data)
          }
          return // Success, exit loop
        } catch (error) {
          if (ignore) return

          if (error.code === 'ECONNABORTED' && i < retry) {
            dispatch({
              type: 'FETCH_TIMEOUT',
              payload: error.message,
              retryCount: i + 1,
            })
            await delay(RETRY_DELAY)
          } else {
            dispatch({ type: 'FETCH_ERROR', payload: getErrorMessage(error) })
            return // Non-retryable error, exit loop
          }
        }
      }
    }
    requestAuth()

    return () => {
      ignore = true
    }
  }, [retry, dispatch])

  useEffect(() => {
    if (!videoId) return
    videoId.on('phaseStarting', handlePhaseStarting)
    videoId.on('completed', handleCompleted)
    videoId.on('failed', handleFailed)

    videoId.turnOn()

    if (start && auth) {
      videoId.start({
        authorization: auth.authorization,
        idType: idType,
      })
    }
  }, [
    videoId,
    start,
    auth,
    idType,
    handlePhaseStarting,
    handleCompleted,
    handleFailed,
  ])

  return (
    <StyledDiv>
      <div id="video" {...props} />
      {auth && auth.id && <i id="auth-id">id: {auth.id}</i>}
    </StyledDiv>
  )
}

VideoID.defaultProps = {
  retry: 3,
}

VideoID.propTypes = {
  retry: PropTypes.number,
  start: PropTypes.bool.isRequired,
  idType: PropTypes.string.isRequired,
  onPhaseStarting: PropTypes.func,
  onCompleted: PropTypes.func,
  onError: PropTypes.func,
  onTimeout: PropTypes.func,
  onFailed: PropTypes.func,
}

export default VideoID
